import * as React from 'react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../../components/ui/select';
import { Input } from '../../../components/ui/input';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { ErrorState } from '../../../design-system/components/ErrorState';
import { NoAccessState } from '../../../design-system/components/NoAccessState';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { useApi } from '../../../design-system/useApi';
import { ScreenshotCard } from '../components/ScreenshotCard';
import { AgentStatusPill } from '../components/AgentStatusPill';
import { LiveViewPanel } from '../components/LiveViewPanel';
import { usePoll } from '../usePoll';
import { monitoringApi } from '../api';
import { Radio } from 'lucide-react';
import type { Screenshot, ScreenshotsPage, LiveStatusResponse } from '../types';
import type { EmployeeWithUser } from '../../hr/types';

type Preset = 'today' | 'yesterday' | 'last7';

function rangeFor(preset: Preset): { from: string; to: string } {
  const now = new Date();
  const today = now.toISOString().slice(0, 10);
  const yesterday = new Date(now.getTime() - 24*3600*1000).toISOString().slice(0, 10);
  const sevenAgo = new Date(now.getTime() - 7*24*3600*1000).toISOString().slice(0, 10);
  if (preset === 'today')     return { from: today,     to: tomorrow(today) };
  if (preset === 'yesterday') return { from: yesterday, to: today };
  return { from: sevenAgo, to: tomorrow(today) };
}
function tomorrow(yyyymmdd: string): string {
  const d = new Date(yyyymmdd + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
}

export function MonitoringPage() {
  // Probe permission: 200 → owner/manager mode, 403 → employee self-view.
  // Anything else (network error, 500) is a real failure — say so instead of
  // silently falling back to the employee view.
  const probe = useApi<ScreenshotsPage>('/api/monitoring/screenshots?limit=1');

  if (probe.loading) return <LoadingState variant="page" />;
  if (probe.status === 403) return <EmployeeView />;
  if (probe.error) {
    return (
      <div>
        <PageHeader
          eyebrow="Work / Monitoring"
          title="Activity capture"
          description="Browse the screenshots your team's desktop agent has uploaded."
        />
        <div className="px-6 py-4 max-w-7xl">
          <ErrorState title="Couldn't load monitoring" description={probe.error} onRetry={probe.reload} />
        </div>
      </div>
    );
  }
  return <OwnerView />;
}

function OwnerView() {
  const empQ = useApi<{ employees: EmployeeWithUser[] }>('/api/hr/employees');
  const employees = empQ.data?.employees ?? [];
  const [selectedEmployeeId, setSelectedEmployeeId] = React.useState<string>('');
  const [preset, setPreset] = React.useState<Preset>('today');
  const [appTitle, setAppTitle] = React.useState('');
  const [appTitleQuery, setAppTitleQuery] = React.useState('');
  const [liveOpen, setLiveOpen] = React.useState(false);
  const [liveStatus, setLiveStatus] = React.useState<LiveStatusResponse | null>(null);

  // Debounce the free-text app/window-title filter so we don't re-query on every keystroke.
  React.useEffect(() => {
    const t = setTimeout(() => setAppTitleQuery(appTitle.trim()), 400);
    return () => clearTimeout(t);
  }, [appTitle]);

  React.useEffect(() => {
    if (!selectedEmployeeId && employees.length > 0) setSelectedEmployeeId(employees[0].id);
  }, [employees, selectedEmployeeId]);

  // Reset cached status when switching employees so the pill never shows stale data.
  React.useEffect(() => { setLiveStatus(null); }, [selectedEmployeeId]);

  // Lazy 20s status poll for the selected employee while the page is open
  // (pauses automatically when the tab is hidden).
  usePoll(
    async () => {
      if (!selectedEmployeeId) return;
      try {
        setLiveStatus(await monitoringApi.liveStatus(selectedEmployeeId));
      } catch {
        // transient — keep last status
      }
    },
    20_000,
    Boolean(selectedEmployeeId),
  );

  const selectedEmployee = employees.find((e) => e.id === selectedEmployeeId);

  const { from, to } = rangeFor(preset);
  const shotsQ = useApi<ScreenshotsPage>(
    selectedEmployeeId
      ? `/api/monitoring/screenshots?employee_id=${selectedEmployeeId}&from=${from}&to=${to}&limit=200${appTitleQuery ? `&app_title=${encodeURIComponent(appTitleQuery)}` : ''}`
      : null,
    [selectedEmployeeId, preset, appTitleQuery],
  );
  const rows = shotsQ.data?.rows ?? [];

  return (
    <div>
      <PageHeader
        eyebrow="Work / Monitoring"
        title="Activity capture"
        description="Browse the screenshots your team's desktop agent has uploaded."
      />
      <div className="px-6 py-4 grid gap-4 max-w-7xl">
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-sm text-muted-foreground">Employee:</span>
          <Select value={selectedEmployeeId} onValueChange={setSelectedEmployeeId}>
            <SelectTrigger className="w-64"><SelectValue placeholder="Select employee" /></SelectTrigger>
            <SelectContent>
              {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
            </SelectContent>
          </Select>

          {selectedEmployeeId && liveStatus && (
            <AgentStatusPill
              status={liveStatus.agent.status}
              lastHeartbeatAt={liveStatus.agent.last_heartbeat_at}
            />
          )}

          <Button
            size="sm"
            variant="outline"
            disabled={!selectedEmployeeId}
            onClick={() => setLiveOpen(true)}
            className="gap-1.5"
          >
            <Radio className="h-3.5 w-3.5" />
            Live view
          </Button>

          <Input
            value={appTitle}
            onChange={(e) => setAppTitle(e.target.value)}
            placeholder="Filter by app / window title"
            className="w-64"
          />

          <div className="flex gap-1 ml-auto">
            <PresetButton active={preset === 'today'}     onClick={() => setPreset('today')}>Today</PresetButton>
            <PresetButton active={preset === 'yesterday'} onClick={() => setPreset('yesterday')}>Yesterday</PresetButton>
            <PresetButton active={preset === 'last7'}     onClick={() => setPreset('last7')}>Last 7 days</PresetButton>
          </div>
        </div>

        {shotsQ.loading && <LoadingState variant="inline" />}
        {!shotsQ.loading && shotsQ.error && (
          <ErrorState title="Couldn't load screenshots" description={shotsQ.error} onRetry={shotsQ.reload} />
        )}
        {!shotsQ.loading && !shotsQ.error && rows.length === 0 && (
          <EmptyState title="No screenshots in range" description="Pick a different range or wait for the agent to upload." />
        )}
        <ScreenshotGrid rows={rows} onDeleted={shotsQ.reload} />
      </div>

      {selectedEmployeeId && (
        <LiveViewPanel
          open={liveOpen}
          onOpenChange={setLiveOpen}
          employeeId={selectedEmployeeId}
          employeeName={selectedEmployee?.name ?? 'Employee'}
        />
      )}
    </div>
  );
}

function EmployeeView() {
  const [preset, setPreset] = React.useState<Preset>('today');
  const { from, to } = rangeFor(preset);
  const shotsQ = useApi<ScreenshotsPage>(
    `/api/monitoring/me/screenshots?from=${from}&to=${to}&limit=200`,
    [preset],
  );
  const rows = shotsQ.data?.rows ?? [];

  return (
    <div>
      <PageHeader eyebrow="My account" title="My capture log" description="The screenshots your agent has uploaded today." />
      <div className="px-6 py-4 grid gap-4 max-w-7xl">
        <div className="flex gap-1 ml-auto">
          <PresetButton active={preset === 'today'}     onClick={() => setPreset('today')}>Today</PresetButton>
          <PresetButton active={preset === 'yesterday'} onClick={() => setPreset('yesterday')}>Yesterday</PresetButton>
          <PresetButton active={preset === 'last7'}     onClick={() => setPreset('last7')}>Last 7 days</PresetButton>
        </div>

        {shotsQ.loading && <LoadingState variant="inline" />}
        {!shotsQ.loading && shotsQ.status === 403 && (
          <NoAccessState
            title="No capture log for your account"
            description="Your account isn't linked to an employee with monitoring enabled, so there's nothing to show here."
          />
        )}
        {!shotsQ.loading && shotsQ.error && shotsQ.status !== 403 && (
          <ErrorState title="Couldn't load your capture log" description={shotsQ.error} onRetry={shotsQ.reload} />
        )}
        {!shotsQ.loading && !shotsQ.error && rows.length === 0 && (
          <EmptyState title="No screenshots" description="Your agent hasn't uploaded any captures in this range." />
        )}
        <ScreenshotGrid rows={rows} />
      </div>
    </div>
  );
}

function ScreenshotGrid({ rows, onDeleted }: { rows: Screenshot[]; onDeleted?: () => void }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {rows.map((s) => <ScreenshotCard key={s.id} screenshot={s} onDeleted={onDeleted} />)}
    </div>
  );
}

function PresetButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <Button size="sm" variant={active ? 'default' : 'outline'} onClick={onClick}>{children}</Button>
  );
}
